import React, { useState, useEffect } from 'react';
import LineChartComponent from '@/components/charts/Line';
import Summary from '@/components/Summary';

interface IncomeEvaluationsProps {
  selectedMetrics: {
    industryDistribution?: boolean;
    [key: string]: boolean | undefined;
  };
  totalData: any;
}

const IncomeEvaluations: React.FC<IncomeEvaluationsProps> = ({ selectedMetrics, totalData }) => {
  const [currentSelectedMetrics, setCurrentSelectedMetrics] = useState<{
    industryDistribution?: boolean;
    [key: string]: boolean | undefined;
  }>({});

  // 图表数据状态
  const [chartData, setChartData] = useState({
    totalReturn: [
      { value: 0 },
      { value: 20 },
      { value: 35 },
      { value: 45 },
      { value: 60 },
      { value: 85 }
    ],
    excessReturn: [{ value: 0 }, { value: 15 }, { value: 25 }, { value: 32 }],
    winRate: [{ value: 0 }, { value: 25 }, { value: 45 }, { value: 68 }],
    profitLoss: [{ value: 0 }, { value: 0.5 }, { value: 1.2 }, { value: 1.8 }, { value: 2.1 }]
  });

  useEffect(() => {
    setCurrentSelectedMetrics(selectedMetrics);
  }, [selectedMetrics]);

  // 模拟数据变化
  useEffect(() => {
    const interval = setInterval(() => {
      setChartData(prev => ({
        totalReturn: prev.totalReturn.map(item => ({
          value: Math.max(0, item.value + (Math.random() - 0.5) * 10)
        })),
        excessReturn: prev.excessReturn.map(item => ({
          value: Math.max(0, item.value + (Math.random() - 0.5) * 5)
        })),
        winRate: prev.winRate.map(item => ({
          value: Math.max(0, Math.min(100, item.value + (Math.random() - 0.5) * 8))
        })),
        profitLoss: prev.profitLoss.map(item => ({
          value: Math.max(0, item.value + (Math.random() - 0.5) * 0.3)
        }))
      }));
    }, 2000); // 每2秒更新一次数据

    return () => clearInterval(interval);
  }, []);

  const handleCheckboxChange = (checked: boolean) => {
    setCurrentSelectedMetrics(prev => ({
      ...prev,
      industryDistribution: checked
    }));
  };

  return (
    <div className="my-4 rounded-lg border border-[#F0F0F0] p-3">
      {/* 标题和复选框 */}
      <div className="mb-4 flex items-center gap-2">
        <input
          type="checkbox"
          checked={currentSelectedMetrics.industryDistribution || false}
          onChange={e => handleCheckboxChange(e.target.checked)}
          className="h-4 w-4"
        />
        <span className="text-base font-semibold text-[#1F1F1F]">收益评估</span>
      </div>

      {/* 收益评估指标卡片 */}
      <div className="mb-4 grid grid-cols-4 gap-1">
        {/* 总收益率 */}
        <div className="flex flex-col gap-3 rounded-md border border-[#F0F0F0] bg-white p-2">
          <div className="flex flex-col">
            <span className="text-xs text-[#1F1F1F]">总收益率</span>
            <span className="text-base font-semibold text-[#F5222D]">+8.5%</span>
          </div>
          <LineChartComponent data={chartData.totalReturn} stroke="#5959EB" height="42px" />
        </div>

        {/* 超额收益 */}
        <div className="flex flex-col gap-3 rounded-md border border-[#F0F0F0] bg-white p-2">
          <div className="flex flex-col">
            <span className="text-xs text-[#1F1F1F]">超额收益</span>
            <span className="text-base font-semibold text-[#F5222D]">+3.2%</span>
          </div>
          <LineChartComponent data={chartData.excessReturn} stroke="#1677FF" height="34px" />
        </div>

        {/* 胜率 */}
        <div className="flex flex-col gap-3 rounded-md border border-[#F0F0F0] bg-white p-2">
          <div className="flex flex-col">
            <span className="text-xs text-[#1F1F1F]">胜率</span>
            <span className="text-base font-semibold text-[#F5222D]">68%</span>
          </div>
          <LineChartComponent data={chartData.winRate} stroke="#13C2C2" height="36px" />
        </div>

        {/* 盈亏比 */}
        <div className="flex flex-col gap-3 rounded-md border border-[#F0F0F0] bg-white p-2">
          <div className="flex flex-col">
            <span className="text-xs text-[#1F1F1F]">盈亏比</span>
            <span className="text-base font-semibold text-[#F5222D]">2.1</span>
          </div>
          <LineChartComponent data={chartData.profitLoss} stroke="#FA8C16" height="42px" />
        </div>
      </div>

      <Summary isShowSuggestions={false} title="" content={totalData.收益与回撤概览} />
    </div>
  );
};

export default IncomeEvaluations;
